import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Clock, DollarSign, Users, TrendingUp } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MyWeek() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mine, setMine] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [axios.get(`${API}/dashboard/my-week`)];
      if (isAdmin) promises.push(axios.get(`${API}/dashboard/team-week`));
      const results = await Promise.all(promises);
      setMine(results[0].data);
      if (isAdmin) setTeam(results[1].data);
    } catch (e) {
      console.error("Failed to load My Week:", e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7 h-64 flex items-center justify-center" data-testid="my-week-loading">
        <div className="w-7 h-7 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!mine) return null;

  const maxHours = Math.max(...mine.per_day.map((d) => d.hours), 1);
  const weekLabel = formatWeekLabel(mine.week_start, mine.week_end);

  return (
    <div className="space-y-5" data-testid="my-week-section">
      {/* Personal card */}
      <div className="bg-gradient-to-br from-white to-[#F1E4D5] rounded-3xl border border-[#F0E5DE] p-7" data-testid="my-week-personal">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#EA823A] font-semibold mb-1.5">My week</div>
            <h2 className="font-display text-2xl text-[#1F2A2E]">{weekLabel}</h2>
          </div>
          <div className="text-xs text-[#6B7A80]">Your billable activity Mon→Sun</div>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 mb-6">
          <MetricCard icon={Clock} label="Hours this week" value={mine.total_hours.toFixed(2)} suffix="hrs" />
          <MetricCard icon={DollarSign} label="Billed value" value={`$${mine.total_amount.toFixed(2)}`} accent />
          <MetricCard icon={TrendingUp} label="Unbilled (still to invoice)" value={`$${mine.unbilled_amount.toFixed(2)}`} />
          <MetricCard icon={Users} label="Clients touched" value={mine.clients_touched} />
        </div>

        {/* Daily breakdown sparkbar */}
        <div className="bg-white rounded-2xl border border-[#F0E5DE] p-5" data-testid="my-week-chart">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-4">Daily breakdown</div>
          <div className="flex items-end justify-between gap-2 h-32">
            {mine.per_day.map((d, i) => {
              const heightPct = (d.hours / maxHours) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2 group" data-testid={`day-${DAY_LABELS[i].toLowerCase()}`}>
                  <div className="text-[11px] font-mono text-[#284952] opacity-0 group-hover:opacity-100 transition">
                    {d.hours > 0 ? `${d.hours.toFixed(1)}h` : ""}
                  </div>
                  <div
                    className="w-full rounded-t-lg transition-all hover:opacity-80"
                    style={{
                      height: `${Math.max(heightPct, 2)}%`,
                      background: d.hours > 0 ? "linear-gradient(180deg, #E2718A 0%, #EA823A 100%)" : "#F0E5DE",
                      minHeight: "4px",
                    }}
                  />
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{DAY_LABELS[i]}</div>
                </div>
              );
            })}
          </div>
        </div>

        {mine.task_count === 0 && (
          <p className="mt-5 text-sm text-[#6B7A80]" data-testid="my-week-empty">
            No billable tasks yet this week. Log activities under each client to see them here.
          </p>
        )}
      </div>

      {/* Team card, admin only */}
      {isAdmin && team && (
        <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7" data-testid="my-week-team">
          <div className="flex items-end justify-between gap-3 mb-5">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#EA823A] font-semibold mb-1.5">Team this week</div>
              <h2 className="font-display text-2xl text-[#1F2A2E]">Pulse across the team</h2>
            </div>
            <div className="text-xs text-[#6B7A80]">Director only</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr>
                  <Th>Worker</Th>
                  <Th>Role</Th>
                  <Th>Tasks</Th>
                  <Th>Hours</Th>
                  <Th>Billed value</Th>
                  <Th>Unbilled</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0E5DE]">
                {team.workers.map((w) => (
                  <tr key={w.worker_id} className="hover:bg-[#F1E4D5]/50" data-testid={`team-row-${w.worker_id}`}>
                    <td className="px-3 py-3 font-medium text-[#1F2A2E]">{w.worker_name}</td>
                    <td className="px-3 py-3 text-xs text-[#6B7A80] uppercase tracking-wider">{w.role}</td>
                    <td className="px-3 py-3 text-[#284952]">{w.task_count}</td>
                    <td className="px-3 py-3 font-mono">{w.total_hours.toFixed(2)}</td>
                    <td className="px-3 py-3 font-semibold text-[#1F2A2E]">${w.total_amount.toFixed(2)}</td>
                    <td className="px-3 py-3 text-[#A0521A]">${w.unbilled_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, suffix, accent }) {
  return (
    <div className={`rounded-2xl p-4 border ${accent ? "bg-[#FCE7EB] border-[#F4C3CC]" : "bg-white border-[#F0E5DE]"}`}>
      <Icon className={`w-4 h-4 mb-2 ${accent ? "text-[#E2718A]" : "text-[#EA823A]"}`} />
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-0.5">{label}</div>
      <div className="font-display text-xl text-[#1F2A2E]">
        {value}{suffix && <span className="text-xs text-[#6B7A80] ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function formatWeekLabel(start, end) {
  try {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const monthSame = s.getMonth() === e.getMonth();
    const sFmt = s.toLocaleDateString("en-AU", { day: "numeric", month: monthSame ? undefined : "short" });
    const eFmt = e.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
    return `${sFmt}, ${eFmt}`;
  } catch {
    return `${start}, ${end}`;
  }
}
